import { useState } from 'react';

import { DetailedScore, SingSetup, Song } from '~/interfaces';
import { GameTip } from '~/modules/elements/game-tip';
import { AccuracyOverTime } from '~/modules/game-engine/game-state/helpers/accuracy-over-time';
import { NoteStats } from '~/modules/game-engine/game-state/helpers/calculate-note-stats';
import useBackgroundMusic from '~/modules/hooks/use-background-music';
import { PlayerNumber } from '~/modules/players/player-number';
import SongDifficultyChip from '~/routes/game/singing/post-game/song-difficulty-chip';
import ResultsView from '~/routes/game/singing/post-game/views/results';
import { BackgroundThemeSetting, useSettingValue } from '~/routes/settings/settings-state';

import SongPage from '../../song-page';
import HighScoresView from './views/high-scores';

export interface PlayerScore {
  detailedScore: [DetailedScore, DetailedScore];
  playerNumber: PlayerNumber;
  name: string;
  /** How the notes went, as opposed to what they scored. Absent in online mode, which has no
   * access to the other singers' notes. */
  notes?: NoteStats;
  /** How the accuracy moved through the song, for the timeline. Absent for the same reason. */
  accuracyOverTime?: AccuracyOverTime;
}

interface HighScoreEntity {
  singSetupId: string;
  name: string;
  score: number;
  date: string;
}

interface Props {
  width: number;
  height: number;
  song: Song;
  onClickSongSelection: () => void;
  players: PlayerScore[];
  singSetup: SingSetup;
  highScores: HighScoreEntity[];
  /** Online games are not persisted to local high scores, so there is no high-score step to advance to. */
  highScoresEnabled?: boolean;
  /** Online mode hides the camera roll — the singers are not in the same room. */
  cameraEnabled?: boolean;
  /**
   * Whether leaving these screens plays whatever is waiting in the running order. False online,
   * where it returns to the lobby instead — the queue is this device's, and naming a song from it as
   * "next up" there would be announcing something that is not going to happen.
   */
  queueAdvances?: boolean;
  'data-test'?: string;
}

function PostGameView({
  song,
  width,
  height,
  onClickSongSelection,
  players,
  highScores,
  singSetup,
  highScoresEnabled = true,
  cameraEnabled = true,
  queueAdvances = true,
  'data-test': dataTest,
}: Props) {
  const [backgroundTheme] = useSettingValue(BackgroundThemeSetting);
  useBackgroundMusic(true);
  const [step, setStep] = useState<'results' | 'highscores'>('results');

  return (
    <SongPage
      songData={song}
      width={width}
      height={height}
      headerRow={<SongDifficultyChip song={song} tolerance={singSetup.tolerance} />}>
      {/* `min-h-0` so a step whose content is taller than the page shrinks inside it rather than
          growing past the bottom — a flex item defaults to `min-height: auto` and will not. The
          high-scores step relies on it: its boards give up height so the button stays on screen. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2" data-test={dataTest}>
        {step === 'results' && (
          <ResultsView
            onNextStep={() => (highScoresEnabled ? setStep('highscores') : onClickSongSelection())}
            players={players}
            singSetup={singSetup}
            highScores={highScores}
            cameraEnabled={cameraEnabled}
            queueAdvances={queueAdvances}
          />
        )}
        {step === 'highscores' && (
          <HighScoresView
            onNextStep={onClickSongSelection}
            singSetup={singSetup}
            highScores={highScores}
            song={song}
            queueAdvances={queueAdvances}
          />
        )}
        {/* Two lines' worth whichever tip came up: they range from one line to two, and left to size
            itself the band moves the singer panels above it by a whole line between one round and the
            next - enough to squeeze the error graph out of a 2x2 layout on some plays and not others.
            `em` rather than `rem` so the reserved height tracks the font size at each breakpoint.

            The band flexes and the tip inside it does not: a tip is a sentence with `<strong>` in the
            middle of it, and as a flex parent the band would drop the whitespace-only nodes either
            side of them and run the words together. */}
        <div className="2xl:text-md flex h-[calc(2.5em+1rem)] w-full shrink-0 items-center justify-center bg-black/75 px-2 text-xs leading-tight md:text-sm">
          <GameTip data-active="true" className="typography block text-center" />
        </div>
        {backgroundTheme !== 'christmas' && (
          <span className="typography text-xs 2xl:text-sm">
            Background music by{' '}
            <a href="https://www.FesliyanStudios.com" target="_blank" rel="noopener noreferrer">
              www.FesliyanStudios.com
            </a>
          </span>
        )}
        {/* The hint strip is fixed to the bottom 3.5rem of the screen, so the tip and the credit need
            somewhere to stand clear of it - the same spacer the profile page ends with. */}
        <div className="h-14 shrink-0" />
      </div>
    </SongPage>
  );
}
export default PostGameView;
